import { noop, useToast } from "@aircall/tractor";
import { formatSecondsToDuration } from "@dashboard/library";
import { differenceInSeconds } from "date-fns";
import { useTranslation } from "react-i18next";

import i18n from "@/config/i18n.config";
import {
  categorizeSpeakers,
  deductMainSpeakers,
} from "@/helpers/participants/participants.helpers";
import { formatInternational } from "@/helpers/phone_number/phone_number.helpers";
import { formatSpeakerDisplay } from "@/helpers/speaker/speaker.helpers";
import { Call as CustomCall } from "@/states/call/call.utility.decl";
import { Speaker } from "@/states/call/speakers/speakers.utility.decl";
import { useCall } from "../useCall/useCall";

type MainSpeakers = {
  mainAgentId: string | undefined;
  mainExternalNumber: string;
};

type UtteranceSpeakerDisplayArgs = {
  call: CustomCall;
  channel: number;
  mainSpeakers: MainSpeakers | null;
};

function download(filename: string, text: string) {
  const element = document.createElement("a");
  element.setAttribute(
    "href",
    `data:text/plain;charset=utf-8,${encodeURIComponent(text)}`
  );
  element.setAttribute("download", filename);
  element.style.display = "none";

  document.body.appendChild(element);

  element.click();

  document.body.removeChild(element);
}

function formatCreatedAt(createdAt: string, locale: string) {
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "full",
    timeStyle: "long",
  }).format(new Date(createdAt));
}

function formatNameOrNumber(
  person?: { fullName?: string | null; number?: string } | null
) {
  return person?.fullName ?? formatInternational(person?.number ?? "");
}

function appendTotalTime(call: CustomCall) {
  if (!call.durationInSecond || !call.answeredAt || !call.createdAt) {
    return "";
  }

  return `Total time: ${formatSecondsToDuration(
    call.durationInSecond +
      differenceInSeconds(new Date(call.answeredAt), new Date(call.createdAt))
  )}\n\n`;
}

function appendNamedPeople(label: string, people: Speaker[]) {
  if (!people.length) {
    return "";
  }

  return `${label}:\n${people
    .map((person) => `${formatNameOrNumber(person)}\n`)
    .join("")}`;
}

function appendCallParties(call: CustomCall) {
  if (!call.from || !call.to || !call.speakers) {
    return "";
  }

  const { mainAgentId, mainExternalNumber } = deductMainSpeakers({
    type: call.type,
    from: call.from,
    to: call.to,
  });

  const { mainAgent, externalUser, otherAgents, otherUsers } =
    categorizeSpeakers(call.speakers, {
      mainAgentId,
      mainExternalNumber,
    });

  return (
    `${i18n.t("main_agent")}: ${formatNameOrNumber(mainAgent)}\n` +
    `${i18n.t("external_user")}: ${formatNameOrNumber(externalUser)}\n` +
    appendNamedPeople(i18n.t("other_agents"), otherAgents) +
    appendNamedPeople(i18n.t("other_users"), otherUsers)
  );
}

function utteranceSpeakerDisplay({
  call,
  channel,
  mainSpeakers,
}: UtteranceSpeakerDisplayArgs) {
  const speaker = call.speakers?.get(channel);
  if (speaker?.fullName) {
    return speaker.fullName;
  }
  if (speaker?.number) {
    return formatInternational(speaker.number);
  }
  if (!mainSpeakers) {
    return "Unknown Speaker";
  }

  // Fallback: use participant info when speakers map is not available.
  // For voicemails, always use the "from" (who left the message).
  const participant =
    call.type === "voicemail" || channel === 0 ? call.from : call.to;
  return formatNameOrNumber(participant);
}

function appendUtterances(call: CustomCall) {
  const utterances = call.transcription?.content?.utterances;
  if (!utterances?.length) {
    return "";
  }

  const mainSpeakers =
    call.from && call.to
      ? deductMainSpeakers({
          type: call.type,
          from: call.from,
          to: call.to,
        })
      : null;

  let result = `\n${i18n.t("transcription")}:\n`;

  for (const utterance of utterances) {
    if (!utterance) {
      continue;
    }

    const speaker = utteranceSpeakerDisplay({
      call,
      channel: utterance.channel,
      mainSpeakers,
    });
    result += `${formatSecondsToDuration(Math.round(utterance.startTime))} - ${speaker}\n`;
    result += `${utterance.entries.map((entry) => entry.W).join(" ")}\n\n`;
  }

  return result;
}

export function parse(call: CustomCall, locale: string) {
  return (
    `${formatSpeakerDisplay([...(call.speakers?.values() ?? [])])}\n` +
    `${i18n.t("call_context.start_time")}: ${formatCreatedAt(call.createdAt, locale)}\n` +
    appendTotalTime(call) +
    appendCallParties(call) +
    appendUtterances(call)
  ).trim();
}

export function useDownloadTranscription(filename: string) {
  const { showToast } = useToast();
  const { t } = useTranslation();

  const { call } = useCall();

  if (!call?.transcription) {
    return {
      downloadTranscription: noop,
    };
  }

  const downloadTranscription = () => {
    const parsedTranscription = parse(call, i18n.language);

    try {
      download(filename, parsedTranscription);

      showToast({
        variant: "success",
        dismissIn: 3000,
        message: t("toast.success"),
      });
    } catch {
      showToast({
        variant: "critical",
        dismissIn: 3000,
        message: t("toast.download_failed"),
      });
    }
  };

  return {
    downloadTranscription,
  };
}
